/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React from 'react';
import { addDays, formatISO } from 'date-fns';
import classNames from 'classnames';

import { FormattedDate, FormattedMessage } from '@futrli/localisation';

import { DAYS_TO_END_OF_WEEK, PERIOD_TYPE_OPTIONS, PeriodType } from '../../../core/constants';
import { forceLocalDate } from '../../../utils/dates';
import { FY_PREFIX } from '../../../forecast/detail/reports/data-grid/format-rows';
import { isCurrentPeriod, isPastCell } from './current-period/current-period';
import { useDataGridLayout } from './data-grid-layout-context';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';

import grid from './grid.scss';

interface Props {
  headerKeys: string[];
  periodType: PeriodType;
}

const renderCurrentMonth = (date: Date) => {
  //  timeZone="UTC"
  return <FormattedDate value={date} month={'long'} year="numeric" />;
};

const renderCurrentWeek = (date: Date, index: number) => {
  if (index === 0) {
    return <FormattedMessage id="predict.remainder_of_week" defaultMessage="Remainder of week" />;
  } else {
    return <FormattedDate value={date} month={'long'} year="numeric" day="2-digit" />;
  }
};

export const ColumnHeaders: React.FC<Props> = ({ headerKeys, periodType }) => {
  const { forecast, forecastGraphDate } = usePredictionsInfoContext();
  const dataGridLayout = useDataGridLayout();
  const layoutStyles = dataGridLayout
    ? ({
        '--data-grid-column-width': `${dataGridLayout.columnWidth}px`,
        '--data-grid-content-width': `${dataGridLayout.contentWidth}px`
      } as React.CSSProperties)
    : undefined;
  const renderCurrentPeriod = (date: Date, index: number) => {
    return periodType === PERIOD_TYPE_OPTIONS.MONTHLY ? renderCurrentMonth(date) : renderCurrentWeek(date, index);
  };
  const renderRegularPeriod = (date: Date) => {
    return periodType === PERIOD_TYPE_OPTIONS.MONTHLY ? (
      <FormattedDate value={date} month={'short'} year="2-digit" />
    ) : (
      <FormattedDate value={date} month={'short'} year="2-digit" day="2-digit" />
    );
  };

  const updateWeeklyKey = (dateKey: string) => {
    if (dateKey.startsWith(FY_PREFIX)) {
      return dateKey;
    }
    if (periodType !== PERIOD_TYPE_OPTIONS.MONTHLY) {
      // translate weekly dates to be the end of the week
      return formatISO(addDays(forceLocalDate(dateKey), DAYS_TO_END_OF_WEEK), { representation: 'date' });
    }
    // monthly dates do not need to be changed
    return dateKey;
  };

  return (
    <header className={grid.header} style={layoutStyles}>
      {headerKeys.map(updateWeeklyKey).map((dateKey, index) => {
        if (dateKey.startsWith(FY_PREFIX)) {
          return (
            <div
              className={classNames({
                uiTypeLabel: true,
                [grid.headerCell]: true,
                [grid.isPast]: isPastCell(dateKey, periodType, forecastGraphDate)
              })}
              key={dateKey}
            >
              {dateKey}
            </div>
          );
        }
        const localDate = forceLocalDate(dateKey);
        const isCurrent = forecast?.kind !== 'budget' && isCurrentPeriod(dateKey, periodType, forecastGraphDate);
        const classes = classNames({
          uiTypeLabel: true,
          [grid.headerCell]: true,
          [grid.isPast]: isPastCell(dateKey, periodType, forecastGraphDate),
          [grid.isCurrent]: isCurrent
        });

        return (
          <div className={classes} key={dateKey}>
            {isCurrent ? renderCurrentPeriod(localDate, index) : renderRegularPeriod(localDate)}
          </div>
        );
      })}
    </header>
  );
};
